import React, { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Hidden,
  useMediaQuery,
  useTheme,
  Typography,
  Paper,
  Box,
  Container,
} from "@material-ui/core";
import Select from "react-select";
import { pageTitleStyles } from "../../../../mui/styles";
import { useCompany } from "../../../../contexts/CompanyContext";
import { useStructure } from "../../../../contexts/StructureContext";
import { useAuth } from "../../../../contexts/AuthContext";
import BackLink from "../../../shared/BackLink";
import LogASale from "./LogASale";
import FibrePeriodicOrderReport from "./FibrePeriodicOrderReport";
import UnpaidOrderReport from "./UnpaidOrderReport";
import UninstalledOrderReport from "./UninstalledOrderReport";
import CommissionNotPaidReport from "./CommissionNotPaidReport";
import StructureReport from "./StructureReport";
import SalesAssist from "./salesAssist/SalesAssist";
import AgentAssist from "./agent-agent-assist/AgentAssist";
import SupplierLinks from "./supplier-links/SupplierLinks";
import Banner from "../../../global/Banner";

/**
 * Supplier is a React component that displays the details of a selected supplier
 * and provides various functionalities such as viewing reports, links, sales assist,
 * agent support, and sales notes. It utilizes tabs for navigation between these features.
 *
 * @param {object} props - The component props.
 * @param {object} props.match - React Router `match` object containing URL parameters.
 *
 * @returns {React.Component} The Supplier component.
 */
export default function Supplier({ match }) {
  // State to hold the selected supplier's details
  const [supplier, setSupplier] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State to hold impersonation details
  const [impersonation, setImpersonation] = useState([]);

  // State to hold the selected report type
  const [selectedReport, setSelectedReport] = useState({
    label: "Unpaid Order Report",
    value: "unpaid_order_report",
  });

  // State to track the selected tab in the UI
  const [selectedTab, setSelectedTab] = useState(1);
  const [selectedReportTab, setSelectedReportTab] = useState(0);

  // Destructuring the activeSuppliers from the useCompany context
  const { activeSuppliers } = useCompany();
  const { downstream } = useStructure();
  const { currentUser } = useAuth();

  const disableStructureReport = !(
    currentUser.is_admin || currentUser.is_leader
  );

  // Extracting the supplier ID from the match props
  const selectedSupplierId = match.params.supplierId;

  // Filtering the active suppliers to find the selected supplier by ID
  const supplierData = activeSuppliers.filter(
    filteredSupplier => filteredSupplier.id === Number(selectedSupplierId)
  )[0];
  // Helper function to capitalize the first letter of a string
  const transformName = item => {
    return item[0].toUpperCase() + item.slice(1);
  };
  // Mapping the downstream agents for the selected supplier
  const downstreamMap = downstream
    .filter(agent =>
      agent.users_supplier_index.some(
        sup => sup.suppliers_master.id === Number(selectedSupplierId)
      )
    )
    .map(agent => ({
      value: agent.id,
      label: `Z-${agent.id}: ${transformName(agent.first_name)} ${transformName(
        agent.surname
      )}`,
    }));

  // Effect to set the initial tab based on whether the supplier has reports
  useEffect(() => {
    if (!supplier) {
      return;
    }
    if (supplier && !supplier.reports) {
      setSelectedTab(1);
    } else {
      setSelectedTab(0);
    }
  }, [supplier]);

  // Effect to update the supplier state when supplierData changes
  useEffect(() => {
    setSupplier(supplierData);
  }, [supplierData]);

  // Handler for changing the selected tab in the UI
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handler for changing the selected report tab
  const handleReportTabChange = (event, newValue) => {
    setSelectedReportTab(newValue);
  };

  const customSelectStyles = {
    menuPortal: base => ({
      ...base,
      zIndex: 9999,
    }),
    control: base => ({
      ...base,
      borderRadius: 4,
      border: "1px solid #e0e0e0",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #aaa",
      },
    }),
    container: base => ({
      ...base,
      width: "100%",
      maxWidth: "100%",
    }),
  };

  // Report type options for the dropdown
  const reportTypeOptions = [
    {
      label: "Unpaid Order Report",
      value: "unpaid_order_report",
    },
    {
      label: "Uninstalled Order Report",
      value: "uninstalled_order_report",
    },
    {
      label: "Commission Not Paid Report",
      value: "commission_not_paid_report",
    },
  ];

  // Render the appropriate report component based on selectedReport
  const renderActionReport = () => {
    switch (selectedReport.value) {
      case "unpaid_order_report":
        return (
          <UnpaidOrderReport
            impersonation={impersonation.value}
            impersonationName={impersonation.label}
            supplier={supplier.reports_name}
            supplierPrettyName={supplier.reports_display_name}
          />
        );
      case "uninstalled_order_report":
        return (
          <UninstalledOrderReport
            impersonation={impersonation.value}
            impersonationName={impersonation.label}
            supplier={supplier.reports_name}
            supplierPrettyName={supplier.reports_display_name}
          />
        );
      case "commission_not_paid_report":
        return (
          <CommissionNotPaidReport
            impersonation={impersonation.value}
            impersonationName={impersonation.label}
            supplier={supplier.reports_name}
            supplierPrettyName={supplier.reports_display_name}
          />
        );
      default:
        return (
          <UnpaidOrderReport
            impersonation={impersonation.value}
            impersonationName={impersonation.label}
            supplier={supplier.reports_name}
            supplierPrettyName={supplier.reports_display_name}
          />
        );
    }
  };

  return (
    <Container
      disableGutters={isMobile}
      style={{
        padding: isMobile ? "0 8px" : "0 0px",
        width: "100%",
        overflow: "hidden",
        maxWidth: "1450px",
      }}
    >
      <BackLink />

      <Hidden xsDown>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            mb: 2,
          }}
        >
          <Banner />
        </Box>
      </Hidden>

      {supplier ? (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          {!isMobile && (
            <Typography variant="h4" style={pageTitleStyles}>
              {supplier.supplier_name}
            </Typography>
          )}

          <Paper
            elevation={0}
            style={{
              marginBottom: 24,
              borderRadius: 8,
              boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
              width: "100%",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={isMobile ? "on" : "off"}
              indicatorColor="primary"
              textColor="primary"
              style={{
                borderBottom: "1px solid #e0e0e0",
                minHeight: "48px",
                width: "100%",
                overflowX: "auto",
              }}
              TabIndicatorProps={{
                style: {
                  marginLeft: "auto",
                  marginRight: "auto",
                },
              }}
            >
              <Tab
                disabled={!supplier.reports}
                label="Reports"
                style={{
                  minWidth: "auto",
                  padding: "6px 12px",
                  maxWidth: "none",
                }}
              />
              <Tab
                label="Links"
                style={{
                  minWidth: "auto",
                  padding: "6px 12px",
                  maxWidth: "none",
                }}
              />
              <Tab
                disabled={!supplier.sales_assist}
                label="Sales Assist"
                style={{
                  minWidth: "auto",
                  padding: "6px 12px",
                  maxWidth: "none",
                }}
              />
              <Tab
                disabled={!supplier.agent_assist}
                label="Agent Support"
                style={{
                  minWidth: "auto",
                  padding: "6px 12px",
                  maxWidth: "none",
                }}
              />
              <Tab
                disabled={!supplier.order_notes}
                label="Sales notes"
                style={{
                  minWidth: "auto",
                  padding: "6px 12px",
                  maxWidth: "none",
                }}
              />
            </Tabs>

            {selectedTab === 0 && supplier.reports && (
              <Box
                sx={{
                  padding: isMobile ? "8px" : "16px",
                  width: "100%",
                  overflowX: "hidden",
                }}
              >
                <Box sx={{ marginBottom: 24 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Select Downstream Agent:
                  </Typography>
                  <Select
                    menuPortalTarget={document.body}
                    styles={customSelectStyles}
                    placeholder="Select Agent"
                    options={downstreamMap}
                    onChange={v => {
                      setImpersonation(v);
                    }}
                  />
                </Box>

                <Box sx={{ width: "100%" }}>
                  <Tabs
                    value={selectedReportTab}
                    onChange={handleReportTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons={isMobile ? "on" : "off"}
                    style={{
                      marginBottom: 24,
                      width: "100%",
                      overflowX: "auto",
                    }}
                    TabIndicatorProps={{
                      style: {
                        maxWidth: "50px",
                        width: "50px",
                        marginLeft: "auto",
                        marginRight: "auto",
                      },
                    }}
                  >
                    <Tab
                      disabled={!supplier.reports}
                      label="Orders"
                      style={{
                        minWidth: "auto",
                        padding: "6px 12px",
                        maxWidth: "none",
                      }}
                    />
                    <Tab
                      disabled={!supplier.reports}
                      label="Actions"
                      style={{
                        minWidth: "auto",
                        padding: "6px 12px",
                        maxWidth: "none",
                      }}
                    />
                    <Tab
                      disabled={disableStructureReport}
                      label="Structure"
                      style={{
                        minWidth: "auto",
                        padding: "6px 12px",
                        maxWidth: "none",
                      }}
                    />
                  </Tabs>

                  {selectedReportTab === 0 && (
                    <FibrePeriodicOrderReport
                      impersonation={impersonation.value}
                      impersonationName={impersonation.label}
                      supplier={supplier.reports_name}
                      supplierPrettyName={supplier.reports_display_name}
                    />
                  )}

                  {selectedReportTab === 1 && (
                    <>
                      <Box sx={{ marginBottom: 24 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Select Report Type:
                        </Typography>
                        <Select
                          placeholder="Select Report"
                          options={reportTypeOptions}
                          defaultValue={{
                            label: "Unpaid Order Report",
                            value: "unpaid_order_report",
                          }}
                          onChange={v => {
                            setSelectedReport(v);
                          }}
                          styles={customSelectStyles}
                        />
                      </Box>

                      {renderActionReport()}
                    </>
                  )}

                  {selectedReportTab === 2 && (
                    <StructureReport
                      impersonation={impersonation.value}
                      impersonationName={impersonation.label}
                      supplier={supplier.reports_name}
                      supplierPrettyName={supplier.reports_display_name}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Paper>

          {selectedTab === 1 && supplier.id && (
            <SupplierLinks supplierId={supplier.id} />
          )}

          {selectedTab === 2 && <SalesAssist Supplier={supplier.id} />}

          {selectedTab === 3 && <AgentAssist Supplier={supplier.id} />}

          {selectedTab === 4 && supplier.order_notes && (
            <LogASale supplier={supplier} />
          )}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", padding: "40px 0" }}>
          <Typography variant="h5">Loading...</Typography>
        </Box>
      )}
    </Container>
  );
}
